import { useState, useEffect } from "react";
import Turnstile from "react-turnstile";
import { properties } from "../common/properties";
import { createResetPasswordPayload, resetPasswordAction } from "../utils";
import SuccessMessage from "./SuccessMessage";
import alertIcon from "../assets/images/alertIcon.svg";
import pwdEye from "../assets/images/pwdEye.svg";
import pwdEyeSlash from "../assets/images/pwdEyeSlash.svg";

interface resetPasswordProps {
  encryptedData: string;
  siteKey?: string;
  onResetSuccess: (targetUrl: string | null) => void;
}
const ResetPassword = (props: resetPasswordProps) => {
  const { encryptedData, onResetSuccess, siteKey } = props;
  const [newPassword, setNewPassword] = useState<string>("");
  const [cfmNewPassword, setCfmNewPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasCustomError, sethasCustomError] = useState(false);
  const [captchaToken, setcaptchaToken] = useState<any>(false);
  const [captchaTokenMobile, setcaptchaTokenMobile] = useState<any>(false);
  const [errMsg, setErrMsg] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [resHasTargetUrl, setResHasTargetUrl] = useState(true);

  let payload: resetPasswordPayload;

  // Regular expressions for each criteria
  const lengthRegex = /.{10,}/; // At least 10 characters
  const lowercaseRegex = /[a-z]/; // At least one lowercase letter
  const uppercaseRegex = /[A-Z]/; // At least one uppercase letter
  const numberRegex = /\d/; // At least one number
  const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{}|;:',.<>?/\\"]/; // At least one special character

  const handleResetPassword = async (e: any) => {
    e.preventDefault();
    // setErrMsg('');
    const error = handleErrorType();
    if (error) {
      sethasCustomError(true);
      setErrMsg(error);
    } else {
      // Proceed with the password reset process

      if (
        (window.innerWidth > 640 && captchaToken) ||
        (window.innerWidth < 641 && captchaTokenMobile)
      ) {
        const token =
          window.innerWidth > 640 ? captchaToken : captchaTokenMobile;
        payload = createResetPasswordPayload(newPassword, encryptedData, token);
        setNewPassword("");
        setCfmNewPassword("");
        const response = await resetPasswordAction(payload);

        if (response) {
          if (response.result?.targetUrl) {
            setResHasTargetUrl(true);
            onResetSuccess(response.result.targetUrl);
            window.location.href = response.result.targetUrl;
          } else {
            setResHasTargetUrl(false);
          }
        }
      }
    }
    setFormSubmitted(true);
  };

  const handleErrorType = () => {
    if (newPassword.length === 0 || cfmNewPassword.length === 0) {
      return properties.invalid_resetPassword_input;
    } else if (isMetCriteria(newPassword) === false) {
      return properties.password_criteria_error;
    } else if (lengthRegex.test(newPassword) === false) {
      return properties.passwordLength_error;
    } else if (newPassword !== cfmNewPassword) {
      return properties.passwordMismatch_error;
    } else {
      return "";
    }
  };
  function handleNewPasswordChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setNewPassword(event.target.value);
    setFormSubmitted(false);
  }
  function handleCnfNewPasswordChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setCfmNewPassword(event.target.value);
    setFormSubmitted(false);
  }

  const isMetCriteria = (pwdValue: any) => {
    const meetsLowercase = lowercaseRegex.test(pwdValue);
    const meetsUppercase = uppercaseRegex.test(pwdValue);
    const meetsNumber = numberRegex.test(pwdValue);
    const meetsSpecialChar = specialCharRegex.test(pwdValue);

    // Check if at least 3 of the criteria are met
    const criteriaMet =
      [meetsLowercase, meetsUppercase, meetsNumber, meetsSpecialChar].filter(
        Boolean
      ).length >= 3;
    return criteriaMet;
  };

  const isPasswordValid = (pwdValue: any) => {
    // Check if password meets each individual criteria
    const meetsLength = lengthRegex.test(pwdValue);
    const meetsCriteria = isMetCriteria(pwdValue);
    // Return true if all criteria are met
    return meetsLength && meetsCriteria;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="reset-password-section">
      <div className="wiley-forgotPasswordHeader">
        <h1 className="wiley-forgotPasswordHeading">Reset Password</h1>
      </div>
      {hasCustomError && (
        <div className="custom-error-div">
          <img src={alertIcon} alt="!" />
          <div className="error-message">{errMsg}</div>
        </div>
      )}

      {!resHasTargetUrl && (
        <SuccessMessage message={properties.resetPasswordSuccessMessage} />
      )}
      <form
        id="resetPwdForm"
        onSubmit={async (e: any) => {
          await handleResetPassword(e);
        }}
      >
        <div className="form-field password-container">
          <label className="formFieldLabel">New Password</label>
          <input
            type={showPassword ? "text" : "password"}
            value={newPassword}
            className={
              newPassword.length > 0 && formSubmitted
                ? isPasswordValid(newPassword)
                  ? "valid"
                  : "invalid"
                : ""
            }
            onChange={handleNewPasswordChange}
          />
          {newPassword.length > 0 &&
            formSubmitted &&
            (isPasswordValid(newPassword) ? (
              ""
            ) : (
              <img src={alertIcon} alt="!" className="input-alert" />
            ))}
          <span className="toggle-password" onClick={togglePasswordVisibility}>
            {showPassword ? <img src={pwdEye} /> : <img src={pwdEyeSlash} />}
          </span>
        </div>

        <div className="form-field password-container cnf-pwd-section">
          <label className="formFieldLabel">Confirm New Password</label>
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={cfmNewPassword}
            className={
              cfmNewPassword.length > 0 && formSubmitted
                ? newPassword === cfmNewPassword &&
                  isPasswordValid(cfmNewPassword)
                  ? "valid"
                  : "invalid"
                : ""
            }
            onChange={handleCnfNewPasswordChange}
          />
          {cfmNewPassword.length > 0 &&
            formSubmitted &&
            (isPasswordValid(cfmNewPassword) ? (
              ""
            ) : (
              <img
                src={alertIcon}
                alt="!"
                className="input-alert cnf-input-alert"
              />
            ))}
          <span
            className="cnf-pwd toggle-password"
            onClick={toggleConfirmPasswordVisibility}
          >
            {showConfirmPassword ? (
              <img src={pwdEye} />
            ) : (
              <img src={pwdEyeSlash} />
            )}
          </span>

          <div className="criteria-section">
            <p
              className={
                newPassword.length > 0
                  ? lengthRegex.test(newPassword)
                    ? "green minten"
                    : "red minten"
                  : ""
              }
            >
              Password must be a minimum of 10 characters.
            </p>
            <p
              className={
                newPassword.length > 0
                  ? isMetCriteria(newPassword)
                    ? "green minMatch"
                    : "red minMatch"
                  : ""
              }
            >
              Password must contain at least 3 of the following:{" "}
            </p>
            <ul>
              <li
                className={
                  uppercaseRegex.test(newPassword)
                    ? "green isUpperCase"
                    : "isUpperCase"
                }
              >
                an uppercase letter
              </li>
              <li
                className={
                  lowercaseRegex.test(newPassword)
                    ? "green isLowerCase"
                    : "isLowerCase"
                }
              >
                a lowercase letter
              </li>
              <li
                className={
                  numberRegex.test(newPassword) ? "green isNumber" : "isNumber"
                }
              >
                a number
              </li>
              <li
                className={
                  specialCharRegex.test(newPassword)
                    ? "green isSpecialChar"
                    : "isSpecialChar"
                }
              >
                a special character
              </li>
            </ul>
          </div>
        </div>

        <div className="captcha">
          <div className="captchVerfication">
            <div className="captchaHeader">
              {captchaToken
                ? "Verifying you are a human."
                : "Please Verify that you are a human."}
            </div>
            <Turnstile
              sitekey={siteKey ?? ""}
              onVerify={(token) => {
                setcaptchaToken(token);
              }}
            />
          </div>
          <div className="captchVerficationMobile">
            <div className="captchaHeader">
              {captchaTokenMobile
                ? "Verifying you are a human."
                : "Please Verify that you are a human."}
            </div>
            <Turnstile
              sitekey={siteKey ?? ""}
              size="compact"
              onVerify={(token) => {
                setcaptchaTokenMobile(token);
              }}
            />
          </div>
        </div>
        <button type="submit" id="wiley-changePasswordSubmitBtn">
          <span className="wileyCommonBtnText"> Change Password</span>
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
